import { useEffect, useRef, useState, type ButtonHTMLAttributes } from "react";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { usePlayerStore } from "../player/playerStore.js";
import { youtubeEmbedUrl } from "./vodHooks.js";
import { runsOnDevice } from "../api/client.js";

type YouTubeCommand = "playVideo" | "pauseVideo" | "mute" | "unMute";

/**
 * A command to the embed (YouTube's iframe API over postMessage). Addressed to
 * the embed's own origin: it loads from youtube-nocookie.com, and commands sent
 * to youtube.com were dropped by the browser, so the media keys never reached it.
 */
function sendToEmbed(frame: HTMLIFrameElement | null, func: YouTubeCommand): void {
  if (!frame?.src) return;
  frame.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: [] }), new URL(frame.src).origin);
}

interface TrailerButtonProps {
  trailerUrl?: string;
  label?: string;
  /** Controlled mode: the parent supplies state so it can defer its own
   * Back/Escape handling while the trailer is open (layered dialogs). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Extra attributes for the button itself. Home's roving D-pad finds its
   * target by `[data-home-row][data-home-col]`, so a button that sits in one
   * of its rows has to carry them (owner, 2026-09-22: the hero's trailer
   * button could not be reached, and swallowed the press that should have
   * moved past it).
   */
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement> & { [key: `data-${string}`]: string | number | undefined };
}

/**
 * Fullscreen in-app trailer player (TV-first). Renders nothing when there is
 * no trailer. The "Watch Trailer" button opens a fullscreen overlay with the
 * YouTube embed autoplaying. While open:
 * - the app's own player (the dock) pauses and resumes on close if it was
 *   playing, so two streams never overlap audio;
 * - remote media keys (Play/Pause/Stop, MediaPlayPause) and Space control the
 *   trailer via the YouTube JS API;
 * - focus is in the window, on its first button (the website's Sound, else
 *   Close), arrows stay there, and closing puts it back on the button that
 *   opened it. Left unmanaged, focus stayed behind the trailer: arrows moved
 *   it around the details page or the nav rail under the picture, and Back
 *   left it on Resume (owner, 2026-10-07). The iframe is never a stop: the
 *   remote cannot use YouTube's own controls, and the media keys reach it
 *   from the page.
 */
export function TrailerButton({ trailerUrl, label = "▶ Watch Trailer", open: controlledOpen, onOpenChange, buttonProps }: TrailerButtonProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  if (!trailerUrl) return null;
  // With sound (owner, 2026-10-07: "only banner trailers should start at
  // mute"). The viewer's OK on this button is the user gesture that lets an
  // embed start unmuted; the iframe's allow="autoplay" passes it on. The
  // banner's trailer (HomePage) stays muted: nobody asked it to play.
  const embedUrl = youtubeEmbedUrl(trailerUrl, { autoplay: true });
  return (
    <>
      <button
        type="button"
        {...buttonProps}
        className={["ui-btn", "ui-btn--ghost", buttonProps?.className].filter(Boolean).join(" ")}
        onClick={() => setOpen(true)}
      >
        {label}
      </button>
      {embedUrl && open && <TrailerModal embedUrl={embedUrl} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The trailer window, also Social's video player (owner, 2026-10-07: "how can
 * trailers do it. they work perfectly" — a creator's video plays in this same
 * window, and Back closes it). `label` names the window for screen readers.
 * `onUnavailable` hears when YouTube refuses to play the video in an embed
 * (codes 101/150: its owner turned embedding off; 100: gone), so the caller
 * can send it to YouTube's own player instead.
 */
export function TrailerModal({
  embedUrl,
  onClose,
  label = "Trailer",
  onUnavailable,
}: {
  embedUrl: string;
  onClose: () => void;
  label?: string;
  onUnavailable?: (code: number) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  useDialogFocus({ open: true, onClose, rootRef: stageRef });

  // The player reports its errors only to a page that says it is listening.
  const unavailableRef = useRef(onUnavailable);
  unavailableRef.current = onUnavailable;
  const wantsErrors = Boolean(onUnavailable);
  useEffect(() => {
    if (!wantsErrors) return;
    const origin = new URL(embedUrl).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || typeof event.data !== "string") return;
      try {
        const data = JSON.parse(event.data) as { event?: string; info?: unknown };
        if (data.event === "onError" && typeof data.info === "number") unavailableRef.current?.(data.info);
      } catch {
        // Not one of the player's messages.
      }
    };
    window.addEventListener("message", onMessage);
    const listen = () =>
      frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 2, channel: "widget" }), origin);
    const timers = [300, 1000, 2500].map((ms) => window.setTimeout(listen, ms));
    return () => {
      window.removeEventListener("message", onMessage);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [embedUrl, wantsErrors]);

  // THE BROWSER'S SOUND BUTTON (owner, 2026-10-07). The app starts a trailer
  // with sound; a browser may still start it muted (YouTube mutes rather than
  // not play when the browser refuses sound). So on the website a Sound button
  // sits beside Close, focused on open: OK turns the sound on, again turns it
  // off. Its label follows what the player reports. Not in the app.
  const showSound = !runsOnDevice();
  const [muted, setMuted] = useState<boolean | null>(null);
  useEffect(() => {
    if (!showSound) return;
    const origin = new URL(embedUrl).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || typeof event.data !== "string") return;
      try {
        const info = (JSON.parse(event.data) as { info?: { muted?: boolean } }).info;
        if (typeof info?.muted === "boolean") setMuted(info.muted);
      } catch {
        // Not one of the player's messages.
      }
    };
    window.addEventListener("message", onMessage);
    // The player reports only to a page that says it is listening; it may not
    // be ready for the first one.
    const listen = () =>
      frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1, channel: "widget" }), origin);
    const timers = [500, 1500, 3000].map((ms) => window.setTimeout(listen, ms));
    return () => {
      window.removeEventListener("message", onMessage);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [embedUrl, showSound]);
  const toggleSound = () => {
    const on = muted !== false;
    sendToEmbed(frameRef.current, on ? "unMute" : "mute");
    // A browser that refused to start it at all leaves it paused: the press is
    // the gesture that lets it play now.
    if (on) sendToEmbed(frameRef.current, "playVideo");
    setMuted(!on);
  };

  useEffect(() => {
    const send = (func: YouTubeCommand) => sendToEmbed(frameRef.current, func);

    // Pause the app's own player so the trailer's audio never overlaps the
    // stream; resume it on close if it was playing before.
    const store = usePlayerStore.getState();
    const wasPlaying = store.status === "playing";
    if (wasPlaying) store.player?.pause();

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.matches?.("input, textarea, select, [contenteditable]") ?? false;
      switch (event.key) {
        case "MediaPlayPause":
          event.preventDefault();
          send("playVideo");
          break;
        case "MediaPlay":
          event.preventDefault();
          send("playVideo");
          break;
        case "MediaPause":
        case "MediaStop":
          event.preventDefault();
          send("pauseVideo");
          break;
        case " ":
        case "Spacebar":
          // Space toggles the trailer, but only when the iframe isn't already
          // handling it (the embed consumes Space itself once focused).
          if (!typing && !(target?.closest?.("iframe") ?? false)) {
            event.preventDefault();
            send("playVideo");
          }
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (wasPlaying) void store.player?.play().catch(() => undefined);
    };
  }, []);

  return (
    <div className="trailer-modal" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div ref={stageRef} className="trailer-modal__stage" onClick={(event) => event.stopPropagation()}>
        <iframe
          ref={frameRef}
          className="trailer-modal__frame"
          src={embedUrl}
          title={label}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
        {showSound && (
          <button
            type="button"
            className="trailer-modal__sound"
            onClick={toggleSound}
            aria-label={muted === false ? "Mute trailer" : "Turn trailer sound on"}
          >
            {muted === false ? "🔇 Mute" : "🔊 Sound on"}
          </button>
        )}
        <button type="button" className="trailer-modal__close" onClick={onClose} aria-label={`Close ${label.toLowerCase()}`}>
          ✕
        </button>
      </div>
    </div>
  );
}
